import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Plus,
  Search,
  Package,
  Wrench,
  MapPin,
  Clock,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Settings,
  ChevronRight,
  Filter,
  ArrowUpRight
} from 'lucide-react';
import { toast } from 'sonner';
import { equipmentManager, Equipment, Part, MaintenanceRecord } from '../utils/equipment/equipmentManager';

interface EquipmentManagementProps {
  wells: any[];
  currentUser: { id: string; name: string };
  onClose?: () => void;
  isDarkMode?: boolean;
}

export default function EquipmentManagement({ wells, currentUser, onClose, isDarkMode = false }: EquipmentManagementProps) {
  const [view, setView] = useState<'equipment' | 'parts' | 'maintenance'>('equipment');
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [parts, setParts] = useState<Part[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const [showAddEquipment, setShowAddEquipment] = useState(false);
  const [showAddPart, setShowAddPart] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | Equipment['status']>('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setEquipment(equipmentManager.getAllEquipment());
    setParts(equipmentManager.getAllParts());
  };

  const filteredEquipment = equipment.filter(eq => {
    if (filterStatus !== 'all' && eq.status !== filterStatus) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        eq.name.toLowerCase().includes(query) ||
        eq.manufacturer.toLowerCase().includes(query) ||
        eq.serialNumber.toLowerCase().includes(query) ||
        eq.type.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const getStatusColor = (status: Equipment['status']) => {
    switch (status) {
      case 'available': return isDarkMode ? 'bg-green-900/50 text-green-300 border-green-800' : 'bg-green-100 text-green-800 border-green-200';
      case 'in-use': return isDarkMode ? 'bg-blue-900/50 text-blue-300 border-blue-800' : 'bg-blue-100 text-blue-800 border-blue-200';
      case 'maintenance': return isDarkMode ? 'bg-orange-900/50 text-orange-300 border-orange-800' : 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'broken': return isDarkMode ? 'bg-red-900/50 text-red-300 border-red-800' : 'bg-red-100 text-red-800 border-red-200';
      case 'retired': return isDarkMode ? 'bg-slate-800 text-slate-400 border-slate-700' : 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEquipmentIcon = (type: Equipment['type']) => {
    const className = "h-5 w-5";
    switch (type) {
      case 'pump': return <Settings className={className} />;
      case 'compressor': return <Wrench className={className} />;
      case 'motor': return <Settings className={className} />;
      case 'generator': return <Settings className={className} />;
      default: return <Package className={className} />;
    }
  };

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${isDarkMode ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`p-4 shadow-lg z-10 ${isDarkMode ? 'bg-slate-900 border-b border-slate-800' : 'bg-gradient-to-r from-orange-600 to-orange-700 text-white'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-orange-500/20' : 'bg-white/20'}`}>
              <Package className={`h-6 w-6 ${isDarkMode ? 'text-orange-500' : 'text-white'}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold">Equipment Manager</h2>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-orange-100'}`}>Track assets & maintenance</p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-slate-800 text-gray-400 hover:text-white' : 'hover:bg-white/20 active:bg-white/30'}`}
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* View Tabs */}
        <div className={`flex space-x-1 p-1 rounded-xl ${isDarkMode ? 'bg-slate-800/50' : 'bg-black/10'}`}>
          {[
            { id: 'equipment', label: 'Equipment' },
            { id: 'parts', label: 'Parts Inventory' },
            { id: 'maintenance', label: 'Maintenance' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setView(tab.id as any)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-bold transition-all ${view === tab.id
                  ? isDarkMode
                    ? 'bg-orange-600 text-white shadow-lg'
                    : 'bg-white text-orange-600 shadow-md'
                  : isDarkMode
                    ? 'text-gray-400 hover:bg-slate-700 hover:text-white'
                    : 'text-white/80 hover:bg-white/10'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search & Filters */}
      {view === 'equipment' && (
        <div className={`border-b p-4 space-y-3 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-100'}`}>
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, serial, or type..."
              className={`pl-10 h-10 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white placeholder:text-gray-500' : 'bg-gray-50 border-gray-200'}`}
            />
          </div>

          <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-none">
            {[
              { id: 'all', label: 'All Equipment' },
              { id: 'available', label: 'Available' },
              { id: 'in-use', label: 'Active in Field' },
              { id: 'maintenance', label: 'Under Maintenance' }
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setFilterStatus(filter.id as any)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors border ${filterStatus === filter.id
                    ? 'bg-orange-600 border-orange-600 text-white'
                    : isDarkMode
                      ? 'bg-slate-800 border-slate-700 text-gray-400 hover:bg-slate-700 hover:text-white'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
              >
                {filter.label} {filter.id === 'all' && `(${equipment.length})`}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className={`flex-1 overflow-y-auto ${isDarkMode ? 'bg-slate-950' : 'bg-gray-50/50'}`}>
        <AnimatePresence mode="wait">
          {view === 'equipment' && (
            <motion.div
              key="equipment"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-4"
            >
              <EquipmentList
                equipment={filteredEquipment}
                onSelect={setSelectedEquipment}
                getStatusColor={getStatusColor}
                getEquipmentIcon={getEquipmentIcon}
                isDarkMode={isDarkMode}
              />
            </motion.div>
          )}

          {view === 'parts' && (
            <motion.div
              key="parts"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-4"
            >
              <PartsList parts={parts} onUpdate={loadData} isDarkMode={isDarkMode} />
            </motion.div>
          )}

          {view === 'maintenance' && (
            <motion.div
              key="maintenance"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-4"
            >
              <MaintenanceView equipment={equipment} currentUser={currentUser} onUpdate={loadData} isDarkMode={isDarkMode} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Floating Action Button */}
      <motion.div
        className="fixed bottom-6 right-6"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Button
          onClick={() => {
            if (view === 'equipment') setShowAddEquipment(true);
            else if (view === 'parts') setShowAddPart(true);
          }}
          className="h-14 px-6 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-xl rounded-full flex items-center gap-2"
        >
          <Plus className="h-6 w-6" />
          <span className="font-bold text-lg">
            Add {view === 'equipment' ? 'Equipment' : view === 'parts' ? 'Part' : 'Item'}
          </span>
        </Button>
      </motion.div>

      {/* Modals */}
      {selectedEquipment && (
        <EquipmentDetail
          equipment={selectedEquipment}
          wells={wells}
          onClose={() => setSelectedEquipment(null)}
          onUpdate={loadData}
          isDarkMode={isDarkMode}
        />
      )}

      {showAddEquipment && (
        <AddEquipmentForm
          wells={wells}
          onClose={() => setShowAddEquipment(false)}
          onCreate={(data: any) => {
            equipmentManager.addEquipment(data);
            loadData();
            setShowAddEquipment(false);
            toast.success('Equipment added successfully');
          }}
          isDarkMode={isDarkMode}
        />
      )}

      {showAddPart && (
        <AddPartForm
          onClose={() => setShowAddPart(false)}
          onCreate={(data: any) => {
            equipmentManager.addPart(data);
            loadData();
            setShowAddPart(false);
            toast.success('Part added successfully');
          }}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
}

// ---------------- SUB COMPONENTS ----------------

function EquipmentList({ equipment, onSelect, getStatusColor, getEquipmentIcon, isDarkMode }: any) {
  if (equipment.length === 0) {
    return (
      <div className="text-center py-20">
        <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${isDarkMode ? 'bg-slate-900' : 'bg-gray-100'}`}>
          <Package className={`h-10 w-10 ${isDarkMode ? 'text-slate-700' : 'text-gray-300'}`} />
        </div>
        <p className={isDarkMode ? 'text-gray-500' : 'text-gray-500'}>No equipment found matching criteria</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {equipment.map((eq: Equipment, index: number) => (
        <motion.div
          key={eq.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
        >
          <Card
            className={`cursor-pointer hover:shadow-lg transition-all border group ${isDarkMode
                ? 'bg-slate-900 border-slate-800 hover:border-orange-500/50'
                : 'bg-white border-gray-100'
              }`}
            onClick={() => onSelect(eq)}
          >
            <CardContent className="p-4">
              <div className="flex items-start space-x-4">
                <div className={`p-3 rounded-2xl ${isDarkMode ? 'bg-slate-800 text-orange-500' : 'bg-orange-50 text-orange-600'}`}>
                  {getEquipmentIcon(eq.type)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className={`font-bold text-lg truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{eq.name}</h3>
                    <Badge className={`border ${getStatusColor(eq.status)}`}>
                      {eq.status}
                    </Badge>
                  </div>

                  <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    {eq.manufacturer} • {eq.model}
                  </p>

                  <div className={`grid grid-cols-2 gap-2 text-xs p-3 rounded-lg ${isDarkMode ? 'bg-slate-950' : 'bg-gray-50'}`}>
                    <div className="flex items-center space-x-2">
                      <MapPin className={`h-3.5 w-3.5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                      <span className={`truncate font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {eq.location.type === 'well' ? eq.location.wellName : eq.location.type}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <DollarSign className={`h-3.5 w-3.5 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                      <span className={`font-mono font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        ${eq.currentValue.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                <ChevronRight className={`h-5 w-5 self-center ${isDarkMode ? 'text-slate-700 group-hover:text-orange-500' : 'text-gray-300 group-hover:text-orange-500'} transition-colors`} />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  );
}

function PartsList({ parts, onUpdate, isDarkMode }: any) {
  const lowStockParts = parts.filter((p: Part) => p.currentStock <= p.reorderPoint);

  return (
    <div className="space-y-6">
      {lowStockParts.length > 0 && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
          <div className={`rounded-2xl p-4 border ${isDarkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-100'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-600'}`}>
                <AlertCircle className="h-6 w-6" />
              </div>
              <div>
                <h3 className={`font-bold ${isDarkMode ? 'text-red-400' : 'text-red-900'}`}>Low Stock Alert</h3>
                <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                  {lowStockParts.length} part{lowStockParts.length > 1 ? 's' : ''} below reorder point
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      <div className="space-y-3">
        {parts.map((part: Part) => (
          <Card
            key={part.id}
            className={`border ${part.currentStock <= part.reorderPoint
                ? isDarkMode ? 'border-red-800 bg-red-900/10' : 'border-red-200 bg-red-50'
                : isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-gray-100 bg-white'
              }`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{part.name}</h3>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{part.partNumber}</p>
                </div>
                <Badge variant={part.currentStock <= part.reorderPoint ? 'destructive' : 'default'}>
                  Stock: {part.currentStock}
                </Badge>
              </div>

              <div className={`grid grid-cols-2 gap-3 mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-slate-950/50' : 'bg-gray-50'}`}>
                <div>
                  <p className={`text-xs uppercase tracking-wider font-bold mb-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Unit Cost</p>
                  <p className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>${part.unitCost}</p>
                </div>
                <div>
                  <p className={`text-xs uppercase tracking-wider font-bold mb-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Reorder At</p>
                  <p className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{part.reorderPoint}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function MaintenanceView({ equipment, currentUser, onUpdate, isDarkMode }: any) {
  const upcomingMaintenance = equipmentManager.getUpcomingMaintenance(30);
  const overdueMaintenance = equipmentManager.getOverdueMaintenance();

  return (
    <div className="space-y-6">
      {/* Overdue Section */}
      {overdueMaintenance.length > 0 && (
        <section>
          <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
            Overdue Attention Needed
          </h3>
          <div className="space-y-3">
            {overdueMaintenance.map((eq: any) => (
              <div key={eq.id} className={`p-4 rounded-xl border flex justify-between items-center ${isDarkMode ? 'bg-red-900/10 border-red-800' : 'bg-red-50 border-red-100'}`}>
                <div>
                  <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{eq.name}</h4>
                  <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
                    Due: {eq.nextMaintenanceDue && new Date(eq.nextMaintenanceDue).toLocaleDateString()}
                  </p>
                </div>
                <Button size="sm" variant="destructive">Log Service</Button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
          Upcoming (30 Days)
        </h3>
        <div className="space-y-3">
          {upcomingMaintenance.length > 0 ? (
            upcomingMaintenance.map((eq: any) => (
              <div key={eq.id} className={`p-4 rounded-xl border ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-100 shadow-sm'}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                    <Clock className={`h-5 w-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                  </div>
                  <div>
                    <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{eq.name}</h4>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                      Due: {eq.nextMaintenanceDue && new Date(eq.nextMaintenanceDue).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className={isDarkMode ? 'text-slate-500' : 'text-gray-400'}>No upcoming maintenance due.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function EquipmentDetail({ equipment, wells, onClose, onUpdate, isDarkMode }: any) {
  return (
    <div className={`fixed inset-0 z-[60] flex flex-col ${isDarkMode ? 'bg-slate-950' : 'bg-gray-50'}`}>
      <div className={`p-4 ${isDarkMode ? 'bg-slate-900 border-b border-slate-800' : 'bg-gradient-to-r from-orange-600 to-orange-700 text-white'}`}>
        <div className="flex items-center justify-between">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-white'}`}>{equipment.name}</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-black/20 text-white">
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <Card className={isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white'}>
          <CardContent className="p-4 space-y-4">
            <div className={`grid grid-cols-2 gap-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold opacity-70">Manufacturer</label>
                <p className="font-semibold text-lg">{equipment.manufacturer}</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold opacity-70">Model</label>
                <p className="font-semibold text-lg">{equipment.model}</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold opacity-70">Serial</label>
                <p className="font-mono">{equipment.serialNumber}</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold opacity-70">Status</label>
                <p>{equipment.status}</p>
              </div>
            </div>

            <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-slate-950 border-slate-800' : 'bg-gray-50 border-gray-100'}`}>
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="h-4 w-4 opacity-50" />
                <span className="text-xs uppercase tracking-wider font-bold opacity-70">Current Location</span>
              </div>
              <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {equipment.location.type === 'well' ? equipment.location.wellName : equipment.location.type}
              </p>
            </div>
          </CardContent>
        </Card>

        {equipment.maintenanceHistory.length > 0 && (
          <div className="space-y-3">
            <h3 className={`font-bold ml-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Maintenance History</h3>
            {equipment.maintenanceHistory.map((record: MaintenanceRecord) => (
              <Card key={record.id} className={isDarkMode ? 'bg-slate-900 border-slate-800' : ''}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{record.type}</p>
                    <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-slate-800 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                      {new Date(record.date).toLocaleDateString()}
                    </span>
                  </div>
                  <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{record.description}</p>
                  {record.cost > 0 && (
                    <p className={`text-xs font-mono ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Cost: ${record.cost}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AddEquipmentForm({ wells, onClose, onCreate, isDarkMode }: any) {
  const [formData, setFormData] = useState({
    type: 'pump' as Equipment['type'],
    name: '',
    manufacturer: '',
    model: '',
    serialNumber: '',
    purchaseDate: new Date(),
    purchasePrice: 0,
    currentValue: 0,
    status: 'available' as Equipment['status'],
    location: { type: 'warehouse' as const },
    maintenanceHistory: [],
    maintenanceInterval: 90,
  });

  const handleSubmit = () => {
    if (!formData.name || !formData.manufacturer) {
      toast.error('Please fill in required fields');
      return;
    }
    onCreate(formData);
  };

  const inputClass = isDarkMode
    ? 'bg-slate-800 border-slate-700 text-white placeholder:text-gray-500'
    : 'bg-white border-gray-200';

  const labelClass = `block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  return (
    <div className={`fixed inset-0 z-[60] flex flex-col ${isDarkMode ? 'bg-slate-950' : 'bg-gray-50'}`}>
      <div className={`p-4 ${isDarkMode ? 'bg-slate-900 border-b border-slate-800' : 'bg-gradient-to-r from-green-600 to-green-700 text-white'}`}>
        <div className="flex items-center justify-between">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-white'}`}>Add Equipment</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-black/20 text-white">
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <label className={labelClass}>Equipment Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Weatherford RPM-250"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Type</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as Equipment['type'] })}
            className={`w-full h-10 px-3 border rounded-lg ${inputClass}`}
          >
            <option value="pump">Pump</option>
            <option value="compressor">Compressor</option>
            <option value="separator">Separator</option>
            <option value="tank">Tank</option>
            <option value="valve">Valve</option>
            <option value="motor">Motor</option>
            <option value="generator">Generator</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label className={labelClass}>Manufacturer *</label>
          <Input
            value={formData.manufacturer}
            onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
            className={inputClass}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Model</label>
            <Input
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Serial Number</label>
            <Input
              value={formData.serialNumber}
              onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Purchase Price</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
            <Input
              type="number"
              value={formData.purchasePrice}
              onChange={(e) => setFormData({ ...formData, purchasePrice: parseFloat(e.target.value) || 0 })}
              className={`${inputClass} pl-8`}
            />
          </div>
        </div>
      </div>

      <div className={`p-4 border-t ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white'}`}>
        <Button onClick={handleSubmit} className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold text-lg">
          Add Equipment
        </Button>
      </div>
    </div>
  );
}

function AddPartForm({ onClose, onCreate, isDarkMode }: any) {
  const [formData, setFormData] = useState({
    partNumber: '',
    name: '',
    manufacturer: '',
    currentStock: 0,
    reorderPoint: 0,
    unitCost: 0,
  });

  const handleSubmit = () => {
    if (!formData.name || !formData.partNumber) {
      toast.error('Please fill in required fields');
      return;
    }
    onCreate(formData);
  };

  const inputClass = isDarkMode
    ? 'bg-slate-800 border-slate-700 text-white placeholder:text-gray-500'
    : 'bg-white border-gray-200';
  const labelClass = `block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  return (
    <div className={`fixed inset-0 z-[60] flex flex-col ${isDarkMode ? 'bg-slate-950' : 'bg-gray-50'}`}>
      <div className={`p-4 ${isDarkMode ? 'bg-slate-900 border-b border-slate-800' : 'bg-gradient-to-r from-green-600 to-green-700 text-white'}`}>
        <div className="flex items-center justify-between">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-white'}`}>Add Inventory Part</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-black/20 text-white">
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <label className={labelClass}>Part Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Pump Seal Kit"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Part Number *</label>
          <Input
            value={formData.partNumber}
            onChange={(e) => setFormData({ ...formData, partNumber: e.target.value })}
            className={inputClass}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Current Stock</label>
            <Input
              type="number"
              value={formData.currentStock}
              onChange={(e) => setFormData({ ...formData, currentStock: parseInt(e.target.value) || 0 })}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Reorder Point</label>
            <Input
              type="number"
              value={formData.reorderPoint}
              onChange={(e) => setFormData({ ...formData, reorderPoint: parseInt(e.target.value) || 0 })}
              className={inputClass}
            />
          </div>
        </div>
        <div>
          <label className={labelClass}>Unit Cost</label>
          <Input
            type="number"
            value={formData.unitCost}
            onChange={(e) => setFormData({ ...formData, unitCost: parseFloat(e.target.value) || 0 })}
            className={inputClass}
          />
        </div>
      </div>

      <div className={`p-4 border-t ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white'}`}>
        <Button onClick={handleSubmit} className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold text-lg">
          Save Part
        </Button>
      </div>
    </div>
  );
}
